import React, { useEffect } from "react";
import { Modal } from "react-bootstrap";
import {
  Button,
  Box,
  Typography,
  Divider,
  Chip,
  Avatar,
  Stack,
  Paper,
} from "@mui/material";
import {
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Badge as BadgeIcon,
  ContactMail as ContactMailIcon,
  CalendarToday as CalendarTodayIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Assignment as AssignmentIcon,
  AccountBalance as AccountBalanceIcon,
  PersonPinCircleOutlined,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { appColors } from "../../../constant/constant";
import { API } from "../../../api/API";
import ApartmentIcon from "@mui/icons-material/Apartment";
const StatusChip = styled(Chip)(({ theme, active }) => ({
  backgroundColor: active
    ? theme.palette.success.light
    : theme.palette.error.light,
  color: active ? theme.palette.success.dark : theme.palette.error.dark,
  fontWeight: 600,
  fontSize: "0.875rem",
}));

const InfoRow = ({ icon, label, value, fullWidth = false }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "flex-start",
      mb: 2,
      width: fullWidth ? "100%" : "auto",
    }}
  >
    <Box sx={{ mr: 2, color: "text.secondary", mt: 0.5 }}>{icon}</Box>
    <Box>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography variant="body1" sx={{ wordBreak: "break-word" }}>
        {value || "Non renseigné"}
      </Typography>
    </Box>
  </Box>
);

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle1"
    sx={{
      fontWeight: 600,
      mb: 2,
      color: "primary.main",
      display: "flex",
      alignItems: "center",
    }}
  >
    {children}
  </Typography>
);

const ProfilPartenaireModal = ({ show, handleClose, partenaire }) => {
  const getPartenaire = async (id) => {
    try {
      const resp = await API.get(`/partenaires/details/${partenaire.id}`);
      return resp.data;
    } catch (error) {
      console.error("Error fetching partenaire details:", error);
      return null;
    }
  };
  /*
  useEffect(() => {
    if (partenaire) {
      const { id } = partenaire;
      if (id) {
        getPartenaire(id);
      }
    }
  }, [partenaire, show]);*/
  if (!partenaire) return null;

  const {
    intitule,
    is_active,
    email,
    tel,
    adresse,
    image,
    ifu,
    rccm,
    numero,
    date_creation,
    type,
    responsable_full_name,
    createdAt,
  } = partenaire;

  return (
    <Modal
      size="lg"
      show={show}
      onHide={handleClose}
      aria-labelledby="partenaire-modal-title"
      centered
      backdrop="static"
    >
      <Paper sx={{ p: 3 }}>
        <Modal.Header closeButton sx={{ borderBottom: "none", pb: 0 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            {intitule}{" "}
            <StatusChip
              label={is_active ? "Actif" : "Inactif"}
              active={is_active}
              sx={{ ml: 2, verticalAlign: "middle" }}
              size="small"
            />
          </Typography>
        </Modal.Header>

        <Modal.Body>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 4,
              mt: 2,
            }}
          >
            {/* Colonne de gauche - Photo et statut */}
            <Box
              sx={{
                width: { xs: "100%", md: "30%" },
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Avatar
                src={image}
                sx={{
                  width: 120,
                  height: 120,
                  mb: 2,
                  bgcolor: appColors.secondary,
                  fontSize: "3rem",
                }}
              >
                <ApartmentIcon fontSize="large" />
              </Avatar>

              <Box sx={{ width: "100%", textAlign: "center" }}>
                {type && (
                  <Chip
                    label={type}
                    color="success"
                    variant="outlined"
                    sx={{ mb: 1 }}
                  />
                )}
              </Box>
            </Box>

            {/* Colonne de droite - Détails */}
            <Box
              sx={{
                width: { xs: "100%", md: "70%" },
                display: "flex",
                flexDirection: "column",
                gap: 3,
              }}
            >
              {/* Section Informations de contact */}
              <Box>
                <SectionTitle>
                  <ContactMailIcon sx={{ mr: 1, color: appColors.secondary }} />
                  <span className="text-success">Informations de contact</span>
                </SectionTitle>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <InfoRow icon={<EmailIcon />} label="Email" value={email} />
                    <InfoRow
                      icon={<PhoneIcon />}
                      label="Téléphone"
                      value={tel}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <InfoRow
                      icon={<HomeIcon />}
                      label="Adresse"
                      value={adresse}
                      fullWidth
                    />
                  </Box>
                </Stack>
              </Box>

              <Divider />

              {/* Section Informations légales */}
              <Box>
                <SectionTitle>
                  <AccountBalanceIcon
                    sx={{ mr: 1, color: appColors.secondary }}
                    color="success"
                  />
                  <span className="text-success"> Informations légales</span>
                </SectionTitle>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <InfoRow icon={<BadgeIcon />} label="IFU" value={ifu} />
                    <InfoRow
                      icon={<AssignmentIcon />}
                      label="RCCM"
                      value={rccm}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <InfoRow
                      icon={<BusinessIcon />}
                      label="Numéro"
                      value={numero}
                    />
                    {date_creation && (
                      <InfoRow
                        icon={<CalendarTodayIcon />}
                        label="Date de création"
                        value={new Date(date_creation).toLocaleDateString(
                          "fr-FR"
                        )}
                      />
                    )}
                  </Box>
                </Stack>
              </Box>

              {/* Section Représentant légal */}
              {responsable_full_name && (
                <>
                  <Divider />
                  <Box>
                    <SectionTitle>
                      <PersonPinCircleOutlined
                        sx={{ mr: 1, color: appColors.secondary }}
                        color="success"
                      />
                      <p className="text-success"> Représentant légal</p>
                    </SectionTitle>
                    <InfoRow
                      icon={<PersonIcon />}
                      label="Nom"
                      value={responsable_full_name}
                    />
                  </Box>
                </>
              )}
            </Box>
          </Box>
          <Divider />
          <Box>
            <InfoRow
              icon={<CalendarTodayIcon />}
              label="Date de création"
              value={new Date(createdAt).toLocaleDateString("fr-FR")}
            />
          </Box>
        </Modal.Body>

        <Modal.Footer sx={{ borderTop: "none", pt: 0, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            color="success"
            sx={{
              "&:hover": { backgroundColor: "primary.success" },
              px: 4,
              py: 1,
            }}
          >
            Fermer
          </Button>
        </Modal.Footer>
      </Paper>
    </Modal>
  );
};

export default ProfilPartenaireModal;
